import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import { collection, getDocs, query, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import Menu from "../Menu";

export default function MarketingManager() {
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [editingField, setEditingField] = useState(null); // {accountId, fieldName}
  const [editingValue, setEditingValue] = useState("");
  const [postTitle, setPostTitle] = useState("");
  const [postText, setPostText] = useState("");
  const [selectedHashtag, setSelectedHashtag] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [bgUrl, setBgUrl] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "active",
    facebookAccount: "all",
  });

  useEffect(() => {
    getDownloadURL(ref(storage, "media/Brainbackground.png")).then(setBgUrl);
    loadAccounts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [accounts, filters]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      // Don't order by name - we'll sort by lastShared in applyFilters
      const q = query(collection(db, "marketing_accounts"));
      const snapshot = await getDocs(q);
      const accountsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAccounts(accountsData);
    } catch (err) {
      console.error("Error loading accounts:", err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...accounts];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(acc => 
        acc.name?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status !== "all") {
      filtered = filtered.filter(acc => acc.status === filters.status);
    }

    if (filters.facebookAccount !== "all") {
      filtered = filtered.filter(acc => acc.facebookAccount === filters.facebookAccount);
    }

    // Sort by lastShared (oldest first - empty/null first, then by date ascending)
    filtered.sort((a, b) => {
      const aDate = a.lastShared || "";
      const bDate = b.lastShared || "";
      
      // Empty strings come first
      if (!aDate && !bDate) return 0;
      if (!aDate) return -1;
      if (!bDate) return 1;
      
      // Compare dates (older first)
      return aDate.localeCompare(bDate);
    });

    setFilteredAccounts(filtered);
  };


  const handleDeleteAccount = async (accountId, accountName, e) => {
    e.stopPropagation(); // Prevent selecting the account when clicking delete
    
    if (!window.confirm(`Are you sure you want to delete "${accountName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const accountRef = doc(db, "marketing_accounts", accountId);
      await deleteDoc(accountRef);
      await loadAccounts();
      if (expandedAccountId === accountId) {
        setExpandedAccountId(null);
      }
    } catch (err) {
      console.error("Error deleting account:", err);
      alert(`Error: ${err.message}`);
    }
  };

  const formatTimestamp = () => {
    const now = new Date();
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'Australia/Brisbane', // UTC+10 (AEST)
      timeZoneName: 'short'
    };
    const formatted = now.toLocaleString('en-US', options);
    // Format: "November 3, 2025 at 5:39:23 PM GMT+10"
    return formatted.replace(',', ' at').replace('GMT', 'UTC');
  };

  const getDaysSinceLastPost = (lastShared) => {
    if (!lastShared) return null;
    
    try {
      // Try to parse as date string (format: "2025-10-28")
      const dateParts = lastShared.split('-');
      if (dateParts.length === 3) {
        const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
      }
    } catch (err) {
      console.error("Error calculating days since last post:", err);
    }
    return null;
  };

  const getTodayPostsCount = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayDateString = `${year}-${month}-${day}`;
    
    return accounts.filter(account => {
      // Direct string comparison for accuracy (lastShared format: YYYY-MM-DD)
      return account.lastShared === todayDateString;
    }).length;
  };

  const handleFieldEdit = (accountId, fieldName, currentValue, e) => {
    e.stopPropagation();
    setEditingField({ accountId, fieldName });
    setEditingValue(currentValue || "");
  };

  const handleFieldSave = async (accountId, fieldName) => {
    if (!editingField || editingField.accountId !== accountId || editingField.fieldName !== fieldName) return;
    
    try {
      const accountRef = doc(db, "marketing_accounts", accountId);
      let valueToSave;
      
      if (fieldName === "memberCount") {
        valueToSave = parseInt(editingValue, 10) || 0;
      } else if (fieldName === "requiresApproval") {
        valueToSave = editingValue === "true" || editingValue === "yes" || editingValue === "Yes";
      } else if (fieldName === "url" || fieldName === "name" || fieldName === "postTypes" || fieldName === "postTimeRules" || fieldName === "customSpiel" || fieldName === "postType" || fieldName === "lastShared" || fieldName === "facebookAccount") {
        valueToSave = editingValue.trim();
      } else {
        valueToSave = editingValue.trim();
      }
      
      await updateDoc(accountRef, {
        [fieldName]: valueToSave,
      });
      await loadAccounts();
      setEditingField(null);
      setEditingValue("");
    } catch (err) {
      console.error("Error updating field:", err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleFieldCancel = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const handleToggleApproval = async (accountId, currentValue, e) => {
    e.stopPropagation(); // Prevent expanding account when clicking button
    
    try {
      const accountRef = doc(db, "marketing_accounts", accountId);
      await updateDoc(accountRef, {
        requiresApproval: !currentValue,
      });
      await loadAccounts();
    } catch (err) {
      console.error("Error toggling approval:", err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleShareAccount = async (accountId, accountUrl, e) => {
    e.stopPropagation(); // Prevent selecting the account when clicking share
    
    if (!accountUrl || accountUrl.trim() === "") {
      alert("No URL available for this account.");
      return;
    }

    try {
      const accountRef = doc(db, "marketing_accounts", accountId);
      const formattedTimestamp = formatTimestamp();
      
      // Get today's date in YYYY-MM-DD format
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const lastSharedDate = `${year}-${month}-${day}`;
      
      // Build update object
      const updateData = {
        lastShared: lastSharedDate,
        updatedAt: formattedTimestamp,
      };
      
      // Only update postType if postTitle is provided
      if (postTitle.trim()) {
        updateData.postType = postTitle.trim();
      }
      
      await updateDoc(accountRef, updateData);
      await loadAccounts();
    } catch (err) {
      console.error("Error updating account:", err);
      alert(`Error updating account: ${err.message}`);
      return;
    }

    // Open Facebook link in new tab
    window.open(accountUrl, "_blank", "noopener,noreferrer");
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    if (typeof value === "boolean") return String(value);
    // Check if it's a Firestore Timestamp
    if (value && typeof value === "object" && value.toDate) {
      return value.toDate().toLocaleString();
    }
    // Fallback to string conversion
    return String(value);
  };

  const formatLastSharedDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      // Parse YYYY-MM-DD format
      const dateParts = dateString.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2], 10);
        const date = new Date(year, month, day);
        
        // Format as "11 November 2025"
        const options = { day: 'numeric', month: 'long', year: 'numeric' };
        return date.toLocaleDateString('en-GB', options);
      }
    } catch (err) {
      console.error("Error formatting lastShared date:", err);
    }
    
    return dateString; // Return original if parsing fails
  };

  // Hashtag list with approximate post counts
  const hashtags = [
    { tag: "#whatsonbrisbane", count: "78K" },
    { tag: "#northbrisbane", count: "82K" },
    { tag: "#brisbanee", count: "45K" },
    { tag: "#PubQuiz", count: "510K" },
    { tag: "#BrisbaneEvents", count: "159K" },
    { tag: "#thingstodobrisbane", count: "16K" },
    { tag: "#CraftBeerExperience", count: "8K" },
    { tag: "#moretonbayregion", count: "12K" },
  ];

  const handleHashtagSelect = async (e) => {
    const hashtag = e.target.value;
    if (!hashtag) {
      setSelectedHashtag("");
      return;
    }

    setSelectedHashtag(hashtag);

    try {
      // Copy hashtag to clipboard
      await navigator.clipboard.writeText(hashtag);
      // Show brief feedback (optional - you can remove if too annoying)
      const originalBg = e.target.style.background;
      e.target.style.background = "rgba(71,227,115,0.3)";
      setTimeout(() => {
        e.target.style.background = originalBg;
      }, 300);
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement("textarea");
        textArea.value = hashtag;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (fallbackErr) {
        console.error("Failed to copy hashtag:", fallbackErr);
        alert("Failed to copy hashtag. Please copy manually: " + hashtag);
      }
    }

    // Reset selection after copying
    setTimeout(() => {
      setSelectedHashtag("");
      e.target.value = "";
    }, 100);
  };

  const getUniqueFacebookAccounts = () => {
    const accountsSet = new Set();
    accounts.forEach(acc => {
      if (acc.facebookAccount) {
        accountsSet.add(acc.facebookAccount);
      }
    });
    return Array.from(accountsSet).sort();
  };

  const glassCardStyle = {
    background: "rgba(28,34,58,0.67)",
    borderRadius: "1.2em",
    boxShadow: "0 8px 36px 8px rgba(17,17,27,0.3), 0 1.5px 12px rgba(42,238,255,0.1)",
    border: "1px solid rgba(255,255,255,0.12)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  };

  const glassInputStyle = {
    width: "100%",
    padding: "12px 16px",
    fontSize: "14px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.16)",
    borderRadius: "12px",
    color: "#e7f1fd",
    outline: "none",
    transition: "all 0.2s",
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: bgUrl
          ? `linear-gradient(rgba(24,24,40,0.77), rgba(44,16,50,0.82)), url(${bgUrl}) center center / cover no-repeat`
          : "#223b4c",
        fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
        color: "#fff",
      }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <div>Loading marketing accounts...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: bgUrl
          ? `linear-gradient(rgba(24,24,40,0.77), rgba(44,16,50,0.82)), url(${bgUrl}) center center / cover no-repeat`
          : "#223b4c",
        fontFamily: "'Montserrat', 'Open Sans', Arial, sans-serif",
        paddingBottom: "40px",
      }}
    >
      {/* Top Bar with Menu Button */}
      <div
        style={{
          width: "100%",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ffe066",
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          ☰ Menu
        </button>

      </div>

      {/* Sidebar instance */}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      {/* Main Content Card */}
      <div
        style={{
          ...glassCardStyle,
          padding: "44px 54px 38px 54px",
          minWidth: 340,
          maxWidth: "95vw",
          width: "95vw",
          color: "#e7f1fd",
          marginTop: 38,
        }}
      >

        {/* Filters Section */}
        <div style={{ 
          ...glassCardStyle,
          marginBottom: "30px", 
          padding: "24px", 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
        }}>
          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#ffe066" }}>
              Search
            </label>
            <input
              type="text"
              placeholder="Search by name..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              style={glassInputStyle}
              onFocus={(e) => {
                e.target.style.background = "rgba(255,255,255,0.12)";
                e.target.style.borderColor = "rgba(255,255,255,0.3)";
              }}
              onBlur={(e) => {
                e.target.style.background = "rgba(255,255,255,0.08)";
                e.target.style.borderColor = "rgba(255,255,255,0.16)";
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#ffe066" }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={glassInputStyle}
            >
              <option value="all" style={{ background: "#1c223a", color: "#e7f1fd" }}>All</option>
              <option value="active" style={{ background: "#1c223a", color: "#e7f1fd" }}>Active</option>
              <option value="disabled" style={{ background: "#1c223a", color: "#e7f1fd" }}>Disabled</option>
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#ffe066" }}>
              Facebook Account
            </label>
            <select
              value={filters.facebookAccount}
              onChange={(e) => setFilters({ ...filters, facebookAccount: e.target.value })}
              style={glassInputStyle}
            >
              <option value="all" style={{ background: "#1c223a", color: "#e7f1fd" }}>All Accounts</option>
              {getUniqueFacebookAccounts().map(acc => (
                <option key={acc} value={acc} style={{ background: "#1c223a", color: "#e7f1fd" }}>{acc}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Post Generator Section - Top Right */}
        <div style={{ 
          ...glassCardStyle,
          marginBottom: "30px", 
          padding: "24px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
            <h3 style={{ marginTop: 0, marginBottom: 0, color: "#f8faff", fontWeight: 900, fontSize: "1.3rem" }}>
              Generate Post
            </h3>
            {(() => {
              const todayCount = getTodayPostsCount();
              if (todayCount > 0) {
                return (
                  <span
                    style={{
                      padding: "6px 12px",
                      background: "rgba(71,227,115,0.3)",
                      border: "1px solid rgba(71,227,115,0.5)",
                      borderRadius: "12px",
                      color: "#47e373",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                    }}
                    title={`${todayCount} post${todayCount !== 1 ? 's' : ''} made today`}
                  >
                    {todayCount} today
                  </span>
                );
              }
              return null;
            })()}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: "20px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#ffe066" }}>
                Post Title
              </label>
              <select
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                style={glassInputStyle}
                onFocus={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.12)";
                  e.target.style.borderColor = "rgba(255,255,255,0.3)";
                }}
                onBlur={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.08)";
                  e.target.style.borderColor = "rgba(255,255,255,0.16)";
                }}
              >
                <option value="" style={{ background: "#1c223a", color: "#e7f1fd" }}>Select post title...</option>
                <option value="Brendale Weds Trivia" style={{ background: "#1c223a", color: "#e7f1fd" }}>Brendale Weds Trivia</option>
                <option value="Brendale Theme Trivia" style={{ background: "#1c223a", color: "#e7f1fd" }}>Brendale Theme Trivia</option>
                <option value="R and C Theme Trivia" style={{ background: "#1c223a", color: "#e7f1fd" }}>R and C Theme Trivia</option>
                <option value="Upcoming Trivia" style={{ background: "#1c223a", color: "#e7f1fd" }}>Upcoming Trivia</option>
              </select>
              
              <label style={{ display: "block", marginTop: "20px", marginBottom: "8px", fontWeight: 700, color: "#ffe066" }}>
                Hashtags
              </label>
              <select
                value={selectedHashtag}
                onChange={handleHashtagSelect}
                style={glassInputStyle}
                onFocus={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.12)";
                  e.target.style.borderColor = "rgba(255,255,255,0.3)";
                }}
                onBlur={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.08)";
                  e.target.style.borderColor = "rgba(255,255,255,0.16)";
                }}
              >
                <option value="" style={{ background: "#1c223a", color: "#e7f1fd" }}>Select hashtag to copy...</option>
                {hashtags.map(({ tag, count }) => (
                  <option key={tag} value={tag} style={{ background: "#1c223a", color: "#e7f1fd" }}>
                    {tag} ({count} posts)
                  </option>
                ))}
              </select>
              <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", marginTop: "4px", fontStyle: "italic" }}>
                Selected hashtag copied to clipboard
              </div>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#ffe066" }}>
                Suggested Post Text
              </label>
              <textarea
                placeholder="Post text will appear here..."
                value={postText}
                onChange={(e) => setPostText(e.target.value)}
                style={{
                  ...glassInputStyle,
                  minHeight: "100px",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
                onFocus={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.12)";
                  e.target.style.borderColor = "rgba(255,255,255,0.3)";
                }}
                onBlur={(e) => {
                  e.target.style.background = "rgba(255,255,255,0.08)";
                  e.target.style.borderColor = "rgba(255,255,255,0.16)";
                }}
              />
            </div>
          </div>
        </div>

        {/* Accounts List */}
        <div>
          <h2 style={{ marginBottom: "20px", color: "#f8faff", fontSize: "1.5rem", fontWeight: 900 }}>
            Accounts ({filteredAccounts.length})
          </h2>
            <div style={{ 
              ...glassCardStyle,
              maxHeight: "600px", 
              overflowY: "auto",
              padding: 0,
            }}>
              {filteredAccounts.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
                  {accounts.length === 0 ? "No accounts found. Import CSV data to get started!" : "No accounts match your filters."}
                </div>
              ) : (
                filteredAccounts.map((account, idx) => {
                  const isExpanded = expandedAccountId === account.id;
                  
                  return (
                    <div key={account.id}>
                      <div
                        onClick={() => {
                          setExpandedAccountId(isExpanded ? null : account.id);
                        }}
                        style={{
                          padding: "18px 20px",
                          borderBottom: idx < filteredAccounts.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
                          background: isExpanded 
                            ? "rgba(71,227,115,0.25)" 
                            : "transparent",
                          cursor: "pointer",
                          borderLeft: isExpanded ? "4px solid #47e373" : "4px solid transparent",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isExpanded) {
                            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isExpanded) {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between",
                          gap: "12px",
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontWeight: 700, 
                              fontSize: "16px", 
                              marginBottom: "6px", 
                              color: "#f8faff",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              flexWrap: "wrap",
                            }}>
                              <span>{account.name}</span>
                              <span style={{ 
                                fontSize: "0.85rem", 
                                color: "rgba(255,255,255,0.7)", 
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}>
                                {account.type === "group" ? "👥" : "📄"} {account.type}
                                {account.memberCount > 0 && ` • ${account.memberCount.toLocaleString()} members`}
                                {account.facebookAccount && ` • ${account.facebookAccount}`}
                                {account.lastShared && (
                                  <span style={{ color: "rgba(255,255,255,0.5)" }}>
                                    • Last shared: {formatLastSharedDate(account.lastShared)}
                                  </span>
                                )}
                              </span>
                            </div>
                            {account.lastPostedAt && (
                              <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.6)" }}>
                                Last posted: {account.lastPostedAt.toDate ? account.lastPostedAt.toDate().toLocaleDateString() : account.lastPostedAt} ({account.lastPostType || "N/A"})
                              </div>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div style={{ 
                            display: "flex", 
                            gap: "8px",
                            flexShrink: 0,
                            alignItems: "center",
                          }}>
                            {/* Days Since Last Post Badge */}
                            {(() => {
                              const daysSince = getDaysSinceLastPost(account.lastShared);
                              if (daysSince !== null) {
                                return (
                                  <span
                                    style={{
                                      padding: "6px 10px",
                                      background: daysSince > 7 
                                        ? "rgba(255,107,107,0.3)" 
                                        : daysSince > 3 
                                        ? "rgba(255,193,7,0.3)" 
                                        : "rgba(71,227,115,0.3)",
                                      border: `1px solid ${daysSince > 7 
                                        ? "rgba(255,107,107,0.5)" 
                                        : daysSince > 3 
                                        ? "rgba(255,193,7,0.5)" 
                                        : "rgba(71,227,115,0.5)"}`,
                                      borderRadius: "12px",
                                      color: daysSince > 7 
                                        ? "#ff6b6b" 
                                        : daysSince > 3 
                                        ? "#ffc107" 
                                        : "#47e373",
                                      fontWeight: 700,
                                      fontSize: "0.75rem",
                                    }}
                                    title={`${daysSince} day${daysSince !== 1 ? 's' : ''} since last post`}
                                  >
                                    {daysSince}d
                                  </span>
                                );
                              }
                              return (
                                <span
                                  style={{
                                    padding: "6px 10px",
                                    background: "rgba(255,255,255,0.1)",
                                    border: "1px solid rgba(255,255,255,0.2)",
                                    borderRadius: "12px",
                                    color: "rgba(255,255,255,0.6)",
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                  }}
                                  title="No last shared date"
                                >
                                  ?
                                </span>
                              );
                            })()}
                            
                            {/* Approval Toggle Button */}
                            <button
                              onClick={(e) => handleToggleApproval(account.id, account.requiresApproval || false, e)}
                              style={{
                                padding: "8px 12px",
                                background: account.requiresApproval 
                                  ? "rgba(71,227,115,0.2)" 
                                  : "rgba(255,107,107,0.2)",
                                border: `1px solid ${account.requiresApproval 
                                  ? "rgba(71,227,115,0.4)" 
                                  : "rgba(255,107,107,0.4)"}`,
                                borderRadius: "8px",
                                color: account.requiresApproval ? "#47e373" : "#ff6b6b",
                                cursor: "pointer",
                                fontWeight: 700,
                                fontSize: "0.85rem",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                if (account.requiresApproval) {
                                  e.currentTarget.style.background = "rgba(71,227,115,0.3)";
                                  e.currentTarget.style.borderColor = "rgba(71,227,115,0.6)";
                                } else {
                                  e.currentTarget.style.background = "rgba(255,107,107,0.3)";
                                  e.currentTarget.style.borderColor = "rgba(255,107,107,0.6)";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (account.requiresApproval) {
                                  e.currentTarget.style.background = "rgba(71,227,115,0.2)";
                                  e.currentTarget.style.borderColor = "rgba(71,227,115,0.4)";
                                } else {
                                  e.currentTarget.style.background = "rgba(255,107,107,0.2)";
                                  e.currentTarget.style.borderColor = "rgba(255,107,107,0.4)";
                                }
                              }}
                              title={account.requiresApproval ? "Click to set as Not approving" : "Click to set as Approving"}
                            >
                              {account.requiresApproval ? "✓ Approving" : "✗ Not approving"}
                            </button>
                            
                            {account.url && (
                              <button
                                onClick={(e) => handleShareAccount(account.id, account.url, e)}
                                style={{
                                  padding: "8px 12px",
                                  background: "rgba(0,123,255,0.2)",
                                  border: "1px solid rgba(0,123,255,0.4)",
                                  borderRadius: "8px",
                                  color: "#4da6ff",
                                  cursor: "pointer",
                                  fontWeight: 700,
                                  fontSize: "0.85rem",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "rgba(0,123,255,0.3)";
                                  e.currentTarget.style.borderColor = "rgba(0,123,255,0.6)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "rgba(0,123,255,0.2)";
                                  e.currentTarget.style.borderColor = "rgba(0,123,255,0.4)";
                                }}
                                title="Open Facebook link and save post title"
                              >
                                🔗 Share
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDeleteAccount(account.id, account.name, e)}
                              style={{
                                padding: "8px 12px",
                                background: "rgba(255,107,107,0.2)",
                                border: "1px solid rgba(255,107,107,0.4)",
                                borderRadius: "8px",
                                color: "#ff6b6b",
                                cursor: "pointer",
                                fontWeight: 700,
                                fontSize: "0.85rem",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(255,107,107,0.3)";
                                e.currentTarget.style.borderColor = "rgba(255,107,107,0.6)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "rgba(255,107,107,0.2)";
                                e.currentTarget.style.borderColor = "rgba(255,107,107,0.4)";
                              }}
                              title="Delete account"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      {isExpanded && (
                        <div style={{
                          padding: "20px",
                          background: "rgba(0,0,0,0.2)",
                          borderTop: "1px solid rgba(255,255,255,0.1)",
                        }}>
                          <div style={{ fontSize: "0.9rem", lineHeight: "1.8", color: "rgba(255,255,255,0.8)" }}>
                            {/* Name */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Name:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "name" ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "name")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleFieldSave(account.id, "name");
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "name", account.name, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {formatFieldValue(account.name)}
                                </span>
                              )}
                            </div>
                            
                            {/* Type */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Type:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "type" ? (
                                <select
                                  value={editingValue}
                                  onChange={(e) => {
                                    setEditingValue(e.target.value);
                                    handleFieldSave(account.id, "type");
                                  }}
                                  onBlur={() => handleFieldCancel()}
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="group" style={{ background: "#1c223a", color: "#e7f1fd" }}>group</option>
                                  <option value="page" style={{ background: "#1c223a", color: "#e7f1fd" }}>page</option>
                                </select>
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "type", account.type, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {formatFieldValue(account.type)}
                                </span>
                              )}
                            </div>
                            
                            {/* Status */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Status:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "status" ? (
                                <select
                                  value={editingValue}
                                  onChange={(e) => {
                                    setEditingValue(e.target.value);
                                    handleFieldSave(account.id, "status");
                                  }}
                                  onBlur={() => handleFieldCancel()}
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="active" style={{ background: "#1c223a", color: "#e7f1fd" }}>active</option>
                                  <option value="disabled" style={{ background: "#1c223a", color: "#e7f1fd" }}>disabled</option>
                                </select>
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "status", account.status, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {formatFieldValue(account.status)}
                                </span>
                              )}
                            </div>
                            
                            {/* URL */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Link:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "url" ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "url")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleFieldSave(account.id, "url");
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                account.url ? (
                                  <>
                                    <a 
                                      href={account.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{ color: "#47e373", textDecoration: "none", marginRight: "8px" }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {formatFieldValue(account.url)}
                                    </a>
                                    <span 
                                      onClick={(e) => handleFieldEdit(account.id, "url", account.url, e)}
                                      style={{ cursor: "pointer", fontSize: "0.8rem", color: "rgba(255,255,255,0.5)" }}
                                      title="Edit URL"
                                    >
                                      ✏️
                                    </span>
                                  </>
                                ) : (
                                  <span 
                                    onClick={(e) => handleFieldEdit(account.id, "url", "", e)}
                                    style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)", fontStyle: "italic" }}
                                  >
                                    (click to add)
                                  </span>
                                )
                              )}
                            </div>
                            
                            {/* Post Types */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Post Types:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "postTypes" ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "postTypes")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleFieldSave(account.id, "postTypes");
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "postTypes", account.postTypes, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {formatFieldValue(account.postTypes || "(click to add)")}
                                </span>
                              )}
                            </div>
                            
                            {/* Post Time Rules */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Rules:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "postTimeRules" ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "postTimeRules")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleFieldSave(account.id, "postTimeRules");
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "postTimeRules", account.postTimeRules, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {formatFieldValue(account.postTimeRules || "(click to add)")}
                                </span>
                              )}
                            </div>
                            
                            {/* Custom Spiel */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "flex-start", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Custom Spiel:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "customSpiel" ? (
                                <textarea
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "customSpiel")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={{ ...glassInputStyle, minHeight: "80px", width: "100%", maxWidth: "500px" }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "customSpiel", account.customSpiel, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)", whiteSpace: "pre-wrap" }}
                                >
                                  {formatFieldValue(account.customSpiel || "(click to add)")}
                                </span>
                              )}
                            </div>
                            
                            {/* Post Type */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Post Type:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "postType" ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "postType")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleFieldSave(account.id, "postType");
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "postType", account.postType, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {formatFieldValue(account.postType || "(click to add)")}
                                </span>
                              )}
                            </div>
                            
                            {/* Last Shared */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Last Shared:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "lastShared" ? (
                                <input
                                  type="text"
                                  placeholder="YYYY-MM-DD"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "lastShared")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleFieldSave(account.id, "lastShared");
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <>
                                  <span 
                                    onClick={(e) => handleFieldEdit(account.id, "lastShared", account.lastShared, e)}
                                    style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                  >
                                    {account.lastShared ? formatLastSharedDate(account.lastShared) : "(click to add)"}
                                  </span>
                                  {account.url && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(account.url, "_blank", "noopener,noreferrer");
                                      }}
                                      style={{
                                        padding: "4px 8px",
                                        background: "rgba(59,89,152,0.2)",
                                        border: "1px solid rgba(59,89,152,0.4)",
                                        borderRadius: "6px",
                                        color: "#8b9dc3",
                                        cursor: "pointer",
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        marginLeft: "8px",
                                        transition: "all 0.2s",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "rgba(59,89,152,0.3)";
                                        e.currentTarget.style.borderColor = "rgba(59,89,152,0.6)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "rgba(59,89,152,0.2)";
                                        e.currentTarget.style.borderColor = "rgba(59,89,152,0.4)";
                                      }}
                                      title="Open Facebook page"
                                    >
                                      🔗 FB
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                            
                            {/* Member Count */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Member Count:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "memberCount" ? (
                                <input
                                  type="number"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "memberCount")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleFieldSave(account.id, "memberCount");
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "memberCount", account.memberCount, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {account.memberCount !== undefined && account.memberCount !== null ? account.memberCount.toLocaleString() : "(click to add)"}
                                </span>
                              )}
                            </div>
                            
                            {/* Facebook Account */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Facebook Account:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "facebookAccount" ? (
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  onBlur={() => handleFieldSave(account.id, "facebookAccount")}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleFieldSave(account.id, "facebookAccount");
                                    if (e.key === "Escape") handleFieldCancel();
                                  }}
                                  autoFocus
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "facebookAccount", account.facebookAccount, e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {formatFieldValue(account.facebookAccount || "(click to add)")}
                                </span>
                              )}
                            </div>
                            
                            {/* Requires Approval */}
                            <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <strong style={{ color: "#ffe066" }}>Requires Approval:</strong>
                              {editingField?.accountId === account.id && editingField?.fieldName === "requiresApproval" ? (
                                <select
                                  value={editingValue}
                                  onChange={(e) => {
                                    setEditingValue(e.target.value);
                                    handleFieldSave(account.id, "requiresApproval");
                                  }}
                                  onBlur={() => handleFieldCancel()}
                                  style={glassInputStyle}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="false" style={{ background: "#1c223a", color: "#e7f1fd" }}>No</option>
                                  <option value="true" style={{ background: "#1c223a", color: "#e7f1fd" }}>Yes</option>
                                </select>
                              ) : (
                                <span 
                                  onClick={(e) => handleFieldEdit(account.id, "requiresApproval", account.requiresApproval ? "true" : "false", e)}
                                  style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "rgba(255,255,255,0.3)" }}
                                >
                                  {account.requiresApproval ? "Yes" : "No"}
                                </span>
                              )}
                            </div>
                            
                            {/* Updated At (read-only) */}
                            {account.updatedAt && (
                              <div style={{ marginBottom: "8px" }}>
                                <strong style={{ color: "#ffe066" }}>Updated At:</strong> {formatFieldValue(account.updatedAt)}
                              </div>
                            )}
                            
                            {/* Created At (read-only) */}
                            {account.createdAt && (
                              <div style={{ marginBottom: "8px" }}>
                                <strong style={{ color: "#ffe066" }}>Created At:</strong> {formatFieldValue(account.createdAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
      </div>
    </div>
  );
}
